import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, InputAdornment, TextField, Typography } from '@mui/material';
import { Lock as LockIcon, Person as PersonIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTeacherAuth } from '../hooks/useTeacherAuth';
import LanguageSelector from '../components/LanguageSelector';
import LittleLensLogo from '../components/LittleLensLogo';

const TeacherLogin: React.FC = () => {
  const { t } = useTranslation('auth', { keyPrefix: 'teacherPortal.login' });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const { teacher, login, loading, error, clearError } = useTeacherAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (teacher) navigate('/teacher/dashboard', { replace: true });
  }, [teacher, navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    clearError();
    setLocalError(null);
    try {
      const profile = await login(username, password);
      navigate(profile.must_change_password ? '/teacher/change-password' : '/teacher/dashboard');
    } catch (err: any) {
      setLocalError(err.message || t('signInFailed'));
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ position: 'absolute', top: 16, right: 16, color: 'white' }}>
        <LanguageSelector />
      </Box>
      <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card sx={{ width: '100%', maxWidth: 440, borderRadius: 5, boxShadow: '0 24px 48px rgba(15,23,42,0.16)', border: 'none' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4.5 } }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                <LittleLensLogo maxWidth={100} />
              </Box>
              <Typography variant="h4" fontWeight={800} sx={{ color: 'text.primary' }}>{t('title')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('subtitle')}</Typography>
            </Box>
            {(error || localError) && <Alert severity="error" sx={{ mb: 3 }}>{error || localError}</Alert>}
            <Box component="form" onSubmit={handleSubmit}>
              <TextField fullWidth required label={t('username')} value={username} onChange={(e) => setUsername(e.target.value)} margin="normal" InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: 'grey.400' }} /></InputAdornment> }} sx={{ mb: 1.5 }} />
              <TextField fullWidth required type="password" label={t('password')} value={password} onChange={(e) => setPassword(e.target.value)} margin="normal" InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: 'grey.400' }} /></InputAdornment> }} sx={{ mb: 1 }} />
              <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ mt: 2, py: 1.5, fontSize: '0.95rem', background: 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)', boxShadow: '0 4px 14px rgba(37,99,235,0.35)', '&:hover': { background: 'linear-gradient(135deg, #1d4ed8 0%, #0e7490 100%)', boxShadow: '0 6px 20px rgba(37,99,235,0.45)', transform: 'translateY(-1px)' } }}>
                {loading ? <CircularProgress size={22} color="inherit" /> : t('signIn')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default TeacherLogin;
